import { PostgrestSingleResponse, SupabaseClient } from '@supabase/supabase-js'
import { Database, Tables } from "@/supabase/types"
import { TablesInsert, TablesUpdate } from "@/supabase/types"
import { FolderWithElements, FolderWithElementsAndPermissions } from '@/types/base/folder_with_elements'
import { createIsomorphicLogger } from '@/utils/logger/isomorphicLogger'
import { logError } from '@/utils/errorLogging'
import { printObject } from '@/lib/utils'
import { getWorkspaceById } from './workspaces'
import Authuser, { evaluatePermissions } from '@/types/base/authuser'
import { ContentType } from '@/types/base'

const logger = createIsomorphicLogger('db.folders')

export const getFolderById = async (supabase: SupabaseClient<Database>, folderId: string): Promise<FolderWithElements> => {
  const { data: folder, error } = await supabase
    .from("folders")
    .select(
      `
      *,
      folder_elements (*)
    `
    )
    .eq("id", folderId)
    .single()

  if (!folder) {
    logError(logger, error, 'Failed to fetch Folder by ID', { folderId })
    throw new Error(error.message)
  }

  return folder
}

export const getFoldersByWorkspaceId = async (supabase: SupabaseClient<Database>, workspaceId: string): Promise<FolderWithElements[]> => {
  const { data: folders, error } = await supabase
    .from("folders")
    .select(
      `
      *,
      folder_elements (*)
    `
    )
    .eq("workspace_id", workspaceId)

  if (!folders) {
    logError(logger, error, 'Failed to fetch Folders by workspace ID', { workspaceId })
		return []
	}

  return folders
}

export const getWorkspaceFoldersWithPermissions = async (
	supabase: SupabaseClient<Database>,
	authuser: Authuser,
	workspaceId: string
) : Promise<FolderWithElementsAndPermissions[]> => {
	const wsFolders = await getFoldersByWorkspaceId(supabase, workspaceId)
	const workspace = await getWorkspaceById(supabase, workspaceId)
	const result: FolderWithElementsAndPermissions[] = []

	for (const [_i, folder] of wsFolders.entries()) {
		const perms = await evaluatePermissions(supabase, authuser,
			{ type: folder.type as ContentType, context: "Folder", uuid: folder.id, ws: workspace})

		result.push({ ...folder, permissions: perms })
	}

  return result
}

export const createFolder = async (supabase: SupabaseClient<Database>, folder: TablesInsert<"folders">) => {
  const { data: createdFolder, error } = await supabase
    .from("folders")
    .insert([folder])
    .select("*")
    .single()

  if (error) {
    logError(logger, error, 'Failed to create Folder', { folder: printObject(folder) })
    throw new Error(error.message)
  }

  logger.info('Folder created successfully', { createdFolder: printObject(createdFolder) })

	// Constructing the FolderWithElements object
	const folderWithElements: FolderWithElements = {
		...createdFolder,
		folder_elements: [] // Initialize as an empty array
	}

  return folderWithElements
}

export const updateFolder = async (
	supabase: SupabaseClient<Database>,
  folderId: string,
  folder: TablesUpdate<"folders">): Promise<FolderWithElements> => {
  const { data: updatedFolder, error } = await supabase
    .from("folders")
    .update(folder)
    .eq("id", folderId)
    .select(
      `
      *,
      folder_elements (*)
    `
    )
    .single()

  if (error) {
    logError(logger, error, 'Failed to update Folder', { folderId, folder: printObject(folder) })
    throw new Error(error.message)
  }

  return updatedFolder
}

export const createFolderElement = async (supabase: SupabaseClient<Database>, item: {
  user_id: string
  folder_id: string
  element_id: string
}): Promise<FolderWithElements>  => {
  const { data: createdFolderElement, error } = await supabase
    .from("folder_elements")
    .insert([item])
    .select("*")
    .single()

  if (error) {
    logError(logger, error, 'Failed to create Folder element', { item: printObject(item) })
    throw new Error(error.message)
  }

  logger.info('Folder Element created successfully', { createdFolderElement: printObject(createdFolderElement) })
	return getFolderById(supabase, createdFolderElement.folder_id)
}

export const deleteFolder = async (supabase: SupabaseClient<Database>, folderId: string) => {
  const { error } = await supabase.from("folders").delete().eq("id", folderId)

  if (error) {
    logError(logger, error, 'Failed to delete Folder', { folderId })
    throw new Error(error.message)
  }

  return true
}

export const deleteFolderElement = async (
	supabase: SupabaseClient<Database>,
  elementId: string
): Promise<FolderWithElements | null> => {
  const { data: deletedFolderId, error } = await supabase
		.from("folder_elements")
    .delete()
    .eq("element_id", elementId)
		.select("folder_id")

	if (error) {
		if (error.code === 'PGRST116') { // Check for "No rows found" error
			logger.info('No Folder element found for deletion', { elementId })
			return null
		}
		logError(logger, error, 'Failed to delete Folder element', { elementId })
		throw new Error(error.message)
	}

	if (!deletedFolderId || deletedFolderId.length === 0) {
		logger.info('Folder element not found after deletion attempt', { elementId })
		return null
	}

	return getFolderById(supabase, deletedFolderId[0].folder_id)
}

export const getFolderElementByElementId = async (
	supabase: SupabaseClient<Database>,
  elementId: string
) => {
  const { data: folderElement, error } = await supabase
    .from("folder_elements")
    .select(
      `
      folder_id
    `
    )
    .eq("element_id", elementId)
    .single()

  if (!folderElement) {
    logError(logger, error, 'Failed to fetch Folder element by element ID', { elementId })
    throw new Error(error.message)
  }

  return folderElement
}

type TableWithId = {
  [K in keyof Database['public']['Tables']]:
    Database['public']['Tables'][K]['Row'] extends { id: string }
      ? K
      : never
}[keyof Database['public']['Tables']]

export const fetchElementsByIds = async <T extends TableWithId> (
	supabase: SupabaseClient<Database>,
  table: T,
  elements: Tables<"folder_elements">[]
): Promise<Tables<T>[]> => {
  const ids: string[] = elements.map(el => el.element_id)
  if (ids.length === 0) return []

  const { data, error } = await supabase
    .from(table)
    .select('*')
    .in('id', ids as any)

  if (error) {
    logError(logger, error, 'Failed to fetchEleementsByIds', { elements: printObject(elements) })
    throw new Error(error.message)
  }

  return (data ?? []) as Tables<T>[] ?? []
}